import React, { useCallback, useEffect, useMemo, useState } from "react";
import "echarts-gl";
import { Alert, Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { AppAutocomplete, AppButton, AppTextField } from "../../../components/form";
import { AppFormField, AppFormGrid } from "../../../components/layout";
import { GRID_FULL } from "../../../lib/ui/formLayout";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogPrimaryButton,
  AppDialogTitle,
} from "../../../components/dialog";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChatFeatureGate, useChatHost } from "./chatHost";
import ReactECharts from "echarts-for-react";
import { useMaterialUIController } from "../../../context";
import type { ClarificationQuestion, ChatOrchestrationWebSocketPayload } from "../../../services/chatService";
import { extractTextFromResult, parseFlexibleJsonDeep, persistChatWidgetDraft } from "../../../services/chatService";
import { resolveChatClientErrorMessage } from "../../../services/chatErrorMessage";
import { widgetService } from "../../../services/widgetService";
import { dashboardService } from "../../../services/dashboardService";
import type { Widget } from "../../../types/widget";
import type { ReportDashboard } from "../../../types/dashboard";
import { chartLayoutSize, normalizeChartOption } from "../../../utils/chartNormalizers";
import { buildTableHtml, normalizeTablePayload } from "../../../utils/tablePayload";
import InteractiveTableView from "../../../components/InteractiveTableView";
import { formatWidgetOutputLabel, resolveRenderType } from "../../../utils/widgetDisplay";
import { validateWidgetDraftForSave } from "../../../utils/widgetDraftValidation";
import WidgetDraftApprovalEditor from "../../../components/WidgetDraftApprovalEditor";
import WidgetDraftSummary from "../../../components/WidgetDraftSummary";
import {
  applyDraftFormEdits,
  emptyEditableDraftForm,
  resolveEditableDraftForm,
  type EditableDraftFormState,
} from "../../../utils/widgetDraftForm";
import { draftNeedsApproval } from "../../../utils/widgetDraftApproval";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import type { AppSx } from "../../../assets/theme/custom/types";
import MutationDraftPanel from "./MutationDraftPanel";
import ForecastPlanPanel from "./ForecastPlanPanel";
import ForecastResultView from "./ForecastResultView";
import { isForecastResultPayload } from "../../../utils/forecastResultModel";
import { themeHtmlReportDocument } from "../../../utils/themeHtmlReportDocument";

function getResponseText(result: unknown): string {
  const text = extractTextFromResult(result);
  if (text) return text;
  if (result == null) return "";
  if (typeof result === "string") return result;
  try {
    return JSON.stringify(result, null, 2);
  } catch {
    return String(result);
  }
}

function getResponseHtml(result: unknown): string {
  const tableHtml = buildTableHtml(result);
  if (tableHtml) return tableHtml;
  const text = getResponseText(result);
  return text || "";
}

type ChartErrorBoundaryProps = {
  children: React.ReactNode;
  fallbackMessage: string;
  errorSx: AppSx;
};

type ChartErrorBoundaryState = {
  hasError: boolean;
};

class ChartErrorBoundary extends React.Component<ChartErrorBoundaryProps, ChartErrorBoundaryState> {
  constructor(props: ChartErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    // Intentionally suppress verbose stack rendering; we show concise summary in UI.
  }

  render() {
    if (this.state.hasError) {
      return (
        <Typography
          component="div"
          sx={this.props.errorSx}
        >
          {this.props.fallbackMessage}
        </Typography>
      );
    }
    return this.props.children;
  }
}

function extractClarificationQuestions(result: unknown): ClarificationQuestion[] {
  if (!result || typeof result !== "object") return [];
  const questions = (result as Record<string, unknown>).questions;
  if (!Array.isArray(questions)) return [];
  return questions.filter(
    (q): q is ClarificationQuestion =>
      q != null &&
      typeof q === "object" &&
      typeof (q as ClarificationQuestion).id === "string" &&
      typeof (q as ClarificationQuestion).text === "string"
  );
}

function isOtherOption(option: string): boolean {
  const v = option.trim().toLowerCase();
  return v === "other" || v === "diğer" || v === "diger" || v === "başka" || v === "baska" || v === "custom";
}

function resolveQuestionAnswer(
  q: ClarificationQuestion,
  answers: Record<string, string>,
  customByQuestion: Record<string, string>
): string {
  const selected = (answers[q.id] || "").trim();
  if (!selected) return "";
  if (isOtherOption(selected)) {
    return (customByQuestion[q.id] || "").trim();
  }
  return selected;
}

function formatQuestionAnswer(
  q: ClarificationQuestion,
  answers: Record<string, string>,
  customByQuestion: Record<string, string>
): string {
  const answer = resolveQuestionAnswer(q, answers, customByQuestion);
  return answer ? `${q.text}: ${answer}` : "";
}

function isQuestionAnswered(
  q: ClarificationQuestion,
  answers: Record<string, string>,
  customByQuestion: Record<string, string>
): boolean {
  return resolveQuestionAnswer(q, answers, customByQuestion).trim() !== "";
}

function buildClarificationAnswer(
  questions: ClarificationQuestion[],
  answers: Record<string, string>,
  customByQuestion: Record<string, string>,
  composerText?: string,
  originalRequest?: string
): string {
  const lines = questions
    .map((q) => formatQuestionAnswer(q, answers, customByQuestion))
    .filter(Boolean);
  const extra = composerText?.trim();
  if (extra) {
    lines.push(extra);
  }
  const body = lines.join("\n");
  const goal = originalRequest?.trim();
  if (goal) {
    return `Orijinal istek: ${goal}\n${body}`;
  }
  return body;
}

function extractClarificationContext(result: unknown): {
  indexName?: string;
  suggestedTimeField?: string;
  mappingFetched?: boolean;
} {
  if (!result || typeof result !== "object") return {};
  const context = (result as Record<string, unknown>).context;
  if (!context || typeof context !== "object") return {};
  const c = context as Record<string, unknown>;
  return {
    indexName: c.indexName != null ? String(c.indexName) : undefined,
    suggestedTimeField: c.suggestedTimeField != null ? String(c.suggestedTimeField) : undefined,
    mappingFetched: c.mappingFetched === true,
  };
}

function useReportStyles() {
  return useAppCustomStyles().components.report;
}

function ClarificationPanel({
  message,
  result,
  composerText,
  originalRequest,
  onSubmit,
}: {
  message?: string;
  result: unknown;
  /** Free-text from the chat composer, appended when submitting all answers. */
  composerText?: string;
  /** Prior user goal to preserve across clarification follow-up. */
  originalRequest?: string;
  onSubmit?: (answer: string) => void;
}) {
  const { t } = useTranslation();
  const rs = useReportStyles();
  const questions = useMemo(() => extractClarificationQuestions(result), [result]);
  const context = useMemo(() => extractClarificationContext(result), [result]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [customByQuestion, setCustomByQuestion] = useState<Record<string, string>>({});

  const answeredCount = useMemo(
    () => questions.filter((q) => isQuestionAnswered(q, answers, customByQuestion)).length,
    [questions, answers, customByQuestion]
  );

  const canSubmit =
    questions.length > 0 &&
    questions.every((q) => isQuestionAnswered(q, answers, customByQuestion));

  const submitAll = () => {
    if (!canSubmit || !onSubmit) return;
    onSubmit(
      buildClarificationAnswer(questions, answers, customByQuestion, composerText, originalRequest)
    );
  };

  const selectOption = (questionId: string, option: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: option }));
    if (!isOtherOption(option)) {
      setCustomByQuestion((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
    }
  };

  return (
    <Box>
      {message && (
        <Typography component="div" sx={rs.clarificationTitle}>
          {message}
        </Typography>
      )}
      {(context.indexName || context.suggestedTimeField) && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={rs.clarificationChipRow}>
          {context.indexName && (
            <Chip size="small" variant="outlined" label={t("clarification.indexContext", { index: context.indexName })} />
          )}
          {context.suggestedTimeField && (
            <Chip
              size="small"
              variant="outlined"
              color="info"
              label={t("clarification.timeFieldContext", { field: context.suggestedTimeField })}
            />
          )}
          {context.mappingFetched && (
            <Chip size="small" color="success" variant="outlined" label={t("clarification.mappingOk")} />
          )}
        </Stack>
      )}
      {questions.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={rs.clarificationProgress}>
          {t("clarification.progress", { answered: answeredCount, total: questions.length })}
        </Typography>
      )}
      <AppFormGrid spacing="compact">
        {questions.map((q) => {
          const selected = answers[q.id] || "";
          const otherSelected = selected !== "" && isOtherOption(selected);
          const otherValue = customByQuestion[q.id] || "";

          return (
            <AppFormField key={q.id} size={GRID_FULL}>
              <Typography variant="body2" sx={rs.clarificationQuestion}>
                {q.text}
              </Typography>
              {q.options && q.options.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
                  {q.options.map((opt) => (
                    <Chip
                      key={opt}
                      label={opt}
                      clickable
                      color={answers[q.id] === opt ? "primary" : "default"}
                      variant={answers[q.id] === opt ? "filled" : "outlined"}
                      onClick={() => selectOption(q.id, opt)}
                    />
                  ))}
                  {otherSelected && (
                    <AppTextField
                      size="small"
                      autoFocus
                      value={otherValue}
                      placeholder={t("clarification.customAnswer")}
                      onChange={(e) =>
                        setCustomByQuestion((prev) => ({ ...prev, [q.id]: e.target.value }))
                      }
                      sx={rs.clarificationOtherField}
                    />
                  )}
                </Stack>
              ) : (
                <AppTextField
                  size="small"
                  fullWidth
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                />
              )}
            </AppFormField>
          );
        })}
        {questions.length === 0 && (
          <AppFormField size={GRID_FULL}>
            <Typography variant="body2" color="text.secondary">
              {t("clarification.noQuestions")}
            </Typography>
          </AppFormField>
        )}
        {questions.length > 0 && (
          <AppFormField size={GRID_FULL}>
            <Box sx={rs.clarificationSubmitBox}>
              <AppButton
                variant="contained"
                size="small"
                disabled={!canSubmit || !onSubmit}
                onClick={submitAll}
              >
                {t("clarification.send")}
              </AppButton>
            </Box>
          </AppFormField>
        )}
      </AppFormGrid>
    </Box>
  );
}

function resolveWidgetDraft(payload: ChatOrchestrationWebSocketPayload): Widget | null {
  if (payload.widgetDraft && typeof payload.widgetDraft === "object") {
    return payload.widgetDraft as Widget;
  }
  let result: unknown = payload.result;
  if (typeof result === "string") {
    result = parseFlexibleJsonDeep(result);
  }
  if (!result || typeof result !== "object") return null;
  const record = result as Record<string, unknown>;
  if (record.widgetDraft && typeof record.widgetDraft === "object") {
    return record.widgetDraft as Widget;
  }
  if (record.dataSource && typeof record.dataSource === "object") {
    return record as Widget;
  }
  return null;
}

function widgetDraftStorageKey(payload: ChatOrchestrationWebSocketPayload, draft: Widget): string {
  const indices = (draft.dataSource?.indices || []).join(",");
  return `widget-draft:${payload.correlationId || "local"}:${indices}:${draft.name || ""}`;
}

function WidgetDraftPanel({
  message,
  payload,
  onWidgetDraftPersisted,
}: {
  message?: string;
  payload: ChatOrchestrationWebSocketPayload;
  onWidgetDraftPersisted?: (next: {
    widgetDraft: Widget;
    savedWidgetId?: string;
    message?: string;
  }) => void;
}) {
  const { t } = useTranslation();
  const rs = useReportStyles();
  const routerNavigate = useNavigate();
  const { features, openInApp } = useChatHost();
  /** App pages open in place in the Report UI and in a new tab from the embedded chat. */
  const navigate = (path: string) => (openInApp ? openInApp(path) : routerNavigate(path));
  const draft = useMemo(() => resolveWidgetDraft(payload), [payload]);
  const storageKey = useMemo(
    () => (draft ? widgetDraftStorageKey(payload, draft) : ""),
    [payload, draft]
  );

  const [name, setName] = useState("");
  const [form, setForm] = useState<EditableDraftFormState>(emptyEditableDraftForm);
  const [editOpen, setEditOpen] = useState(false);
  const [status, setStatus] = useState<"pending" | "saving" | "saved" | "dismissed">("pending");
  const [error, setError] = useState("");
  const [savedId, setSavedId] = useState<string | undefined>();
  const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false);
  const [dashboards, setDashboards] = useState<ReportDashboard[]>([]);
  const [selectedDashboardId, setSelectedDashboardId] = useState("");
  const [addingToDashboard, setAddingToDashboard] = useState(false);

  type DashboardOption = { id: string; label: string };
  const dashboardOptions = useMemo<DashboardOption[]>(
    () => dashboards.map((d) => ({ id: d.id || "", label: d.name || d.id || "" })),
    [dashboards]
  );

  const needsAutoEdit = useMemo(() => (draft ? draftNeedsApproval(draft) : false), [draft]);
  const showEditor = status === "pending" && (needsAutoEdit || editOpen);

  useEffect(() => {
    if (!draft) return;
    setName(draft.name || "");
    setForm(resolveEditableDraftForm(draft));
    setEditOpen(draftNeedsApproval(draft));
    if (!storageKey) return;
    const existing = sessionStorage.getItem(storageKey);
    if (existing) {
      setSavedId(existing);
      setStatus("saved");
    }
  }, [draft, storageKey]);

  const buildDraftForSave = useCallback((): Widget | null => {
    if (!draft) return null;
    const trimmedName = name.trim();
    return applyDraftFormEdits({ ...draft, name: trimmedName || draft.name }, form);
  }, [draft, name, form]);

  const saveWidget = async () => {
    if (!draft?.dataSource || status === "saved") return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t("widget.nameRequired"));
      return;
    }
    setStatus("saving");
    setError("");
    let candidate: Widget | null = null;
    try {
      candidate = buildDraftForSave();
    } catch (e: unknown) {
      setStatus("pending");
      setError(e instanceof Error ? e.message : String(e));
      return;
    }
    if (!candidate) return;
    const draftErrors = validateWidgetDraftForSave({ ...candidate, name: trimmedName });
    if (draftErrors.length > 0) {
      setStatus("pending");
      setError(draftErrors.join("; "));
      return;
    }
    try {
      const w: Widget = {
        ...candidate,
        name: trimmedName,
        metadata: { ...(candidate.metadata || {}), createdFromChat: true, version: 1 },
      };
      const created = await widgetService.create(w);
      setSavedId(created.id);
      setStatus("saved");
      if (storageKey) {
        sessionStorage.setItem(storageKey, created.id || "1");
      }
      const historyMessageId = payload.messageId;
      if (historyMessageId && payload.correlationId) {
        try {
          await persistChatWidgetDraft({
            messageId: historyMessageId,
            correlationId: payload.correlationId,
            widgetDraft: w,
            message,
            savedWidgetId: created.id,
          });
          onWidgetDraftPersisted?.({
            widgetDraft: w,
            savedWidgetId: created.id,
            message,
          });
        } catch (historyErr: unknown) {
          const historyMsg =
            historyErr instanceof Error ? historyErr.message : String(historyErr);
          window.dispatchEvent(
            new CustomEvent("showGlobalNotification", {
              detail: {
                color: "warning",
                icon: "warning",
                title: t("widget.historyPersistFailed"),
                content: historyMsg,
              },
            })
          );
        }
      }
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("widget.confirmSaved"),
            content: created.name || "",
          },
        })
      );
    } catch (e: unknown) {
      setStatus("pending");
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const openDashboardPicker = async () => {
    setDashboardDialogOpen(true);
    try {
      const res = await dashboardService.list(0, 100);
      setDashboards(res.content || []);
      if (res.content?.length) {
        setSelectedDashboardId(res.content[0].id || "");
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const addToDashboard = async () => {
    if (!savedId || !selectedDashboardId) return;
    setAddingToDashboard(true);
    try {
      await dashboardService.addWidget(selectedDashboardId, savedId);
      setDashboardDialogOpen(false);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("widget.addedToDashboard"),
            content: "",
          },
        })
      );
      navigate(`/dashboards/${selectedDashboardId}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAddingToDashboard(false);
    }
  };

  if (!draft) {
    return (
      <Alert severity="warning" sx={rs.widgetDraftInvalidAlert}>
        {t("widget.autoSaveInvalid")}
      </Alert>
    );
  }

  const outputFormatLabel = draft
    ? formatWidgetOutputLabel(
        showEditor ? { output: { format: form.format, mapping: form.mapping } } : draft
      )
    : "";
  let summaryDraft = draft!;
  if (draft) {
    try {
      summaryDraft = applyDraftFormEdits({ ...draft, name: name.trim() || draft.name }, form);
    } catch {
      summaryDraft = draft;
    }
  }

  return (
    <Box
      sx={rs.widgetDraftPanel}
    >
      <Typography variant="subtitle2" sx={rs.widgetDraftTitle}>
        {t("widget.draftTitle")}
      </Typography>
      {message && (
        <Typography variant="body2" sx={rs.widgetDraftMessage}>
          {message}
        </Typography>
      )}
      <AppFormGrid spacing="compact" sx={rs.widgetDraftFormGrid}>
        <AppFormField size={GRID_FULL}>
          <AppTextField
            size="small"
            fullWidth
            label={t("widget.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={status === "saved" || status === "saving"}
          />
        </AppFormField>

        {status === "pending" && !showEditor && (
          <AppFormField size={GRID_FULL}>
            <WidgetDraftSummary draft={summaryDraft} outputFormatLabel={outputFormatLabel} />
          </AppFormField>
        )}

        {status === "pending" && !needsAutoEdit && (
          <AppFormField size={GRID_FULL}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {showEditor ? (
                <AppButton
                  variant="outlined"
                  size="small"
                  onClick={() => setEditOpen(false)}
                  sx={rs.widgetDraftTextButton}
                >
                  {t("widget.draftCollapseEdit")}
                </AppButton>
              ) : (
                <AppButton
                  variant="outlined"
                  size="small"
                  onClick={() => setEditOpen(true)}
                  sx={rs.widgetDraftTextButton}
                >
                  {t("common.edit")}
                </AppButton>
              )}
            </Stack>
          </AppFormField>
        )}
      </AppFormGrid>

      {showEditor && (
        <Box sx={rs.widgetDraftEditorBox}>
          <WidgetDraftApprovalEditor
            value={form}
            widgetName={name.trim() || draft.name}
            autoExpanded={needsAutoEdit}
            onChange={(next) => {
              setForm(next);
              setError("");
            }}
          />
        </Box>
      )}

      {status === "saved" && (
        <Alert severity="success" sx={rs.widgetDraftAlert}>
          {t("widget.alreadySaved")}
        </Alert>
      )}
      {status === "dismissed" && (
        <Alert severity="info" sx={rs.widgetDraftAlert}>
          {t("widget.confirmDeclined")}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={rs.widgetDraftAlert}>
          {error}
        </Alert>
      )}

      {status === "pending" && !features.saveWidget && (
        <Typography variant="body2" color="text.secondary">
          {t("chat.embed.featureDisabled")}
        </Typography>
      )}

      {status === "pending" && features.saveWidget && (
        <>
          <Typography variant="body2" sx={rs.widgetDraftQuestion}>
            {showEditor ? t("widget.draftConfirmAfterEdit") : t("widget.confirmSaveQuestion")}
          </Typography>
          <Stack direction="row" spacing={1}>
            <AppButton
              variant="contained"
              size="small"
              color="success"
              onClick={saveWidget}
            >
              {t("widget.confirmYes")}
            </AppButton>
            <AppButton variant="outlined" size="small" onClick={() => setStatus("dismissed")}>
              {t("widget.confirmNo")}
            </AppButton>
          </Stack>
        </>
      )}

      {status === "saving" && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">{t("widget.autoSaving")}</Typography>
        </Stack>
      )}

      {status === "saved" && savedId && (
        <Stack direction="row" spacing={1} sx={rs.widgetDraftActions} flexWrap="wrap" useFlexGap>
          {features.addToDashboard && (
            <AppButton variant="contained" size="small" onClick={openDashboardPicker}>
              {t("widget.addToDashboard")}
            </AppButton>
          )}
          <AppButton variant="outlined" size="small" onClick={() => navigate(`/widgets/${savedId}/edit`)}>
            {t("common.edit")}
          </AppButton>
          <AppButton variant="outlined" size="small" onClick={() => navigate("/widgets")}>
            {t("widget.title")}
          </AppButton>
        </Stack>
      )}

      <AppDialog open={dashboardDialogOpen} onClose={() => setDashboardDialogOpen(false)} fullWidth maxWidth="xs">
        <AppDialogTitle>{t("widget.addToDashboard")}</AppDialogTitle>
        <AppDialogContent>
          <AppAutocomplete<DashboardOption>
            label={t("dashboard.title")}
            options={dashboardOptions}
            value={dashboardOptions.find((d) => d.id === selectedDashboardId) ?? null}
            onChange={(_, opt) => setSelectedDashboardId(opt?.id ?? "")}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disableClearable
          />
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton onClick={() => setDashboardDialogOpen(false)}>
            {t("common.cancel")}
          </AppDialogCancelButton>
          <AppDialogPrimaryButton
            disabled={!selectedDashboardId || addingToDashboard}
            onClick={addToDashboard}
          >
            {t("common.save")}
          </AppDialogPrimaryButton>
        </AppDialogActions>
      </AppDialog>
    </Box>
  );
}

export default function ResponseRenderer({
  payload,
  onClarificationAnswer,
  onWidgetDraftPersisted,
  clarificationComposerText,
  clarificationOriginalRequest,
  fitContainer = false,
  onChartClick,
}: {
  payload: ChatOrchestrationWebSocketPayload;
  onClarificationAnswer?: (answer: string) => void;
  onWidgetDraftPersisted?: (next: {
    widgetDraft: Widget;
    savedWidgetId?: string;
    message?: string;
  }) => void;
  /** Chat composer text appended to clarification answers on submit. */
  clarificationComposerText?: string;
  /** Prior user goal prepended when submitting clarification answers. */
  clarificationOriginalRequest?: string;
  /** When true, chart/table content scales to the parent grid cell (dashboard widgets). */
  fitContainer?: boolean;
  /** Dashboard cross-filter / drill-down: fired when user clicks a chart category. */
  onChartClick?: (categoryValue: string) => void;
}) {
  const [controller] = useMaterialUIController();
  const rs = useReportStyles();
  const { darkMode } = controller;
  const { t } = useTranslation();
  const { message, result, error } = payload;
  const type = resolveRenderType(payload.type, payload.outputFormat, result);

  if (error === "QUOTA_EXCEEDED") {
    const resultObj = result && typeof result === "object" ? (result as Record<string, any>) : {};
    const humanMessage =
      typeof resultObj?.message === "string"
        ? resultObj.message
        : t("chat.quotaExhausted");
    const period = resultObj?.period || payload.period;
    const resetDate = resultObj?.resetDate || payload.resetDate;
    return (
      <Box>
        <Typography
          component="div"
          sx={rs.quotaTitle}
        >
          {humanMessage}
        </Typography>
        {(period || resetDate) && (
          <Typography component="div" sx={rs.quotaDetail}>
            {period ? t("chat.quotaPeriod", { period: String(period) }) : ""}
            {period && resetDate ? " - " : ""}
            {resetDate ? t("chat.quotaReset", { date: String(resetDate) }) : ""}
          </Typography>
        )}
      </Box>
    );
  }

  if (error === "PACKAGE_QUERY_DETAIL_UNAVAILABLE") {
    const resultObj = result && typeof result === "object" ? (result as Record<string, any>) : {};
    const packageLabel =
      typeof resultObj?.packageLabel === "string"
        ? resultObj.packageLabel
        : typeof resultObj?.packageType === "string"
          ? String(resultObj.packageType)
          : "";
    return (
      <Box>
        <Typography component="div" sx={rs.quotaTitle}>
          {t("chat.packageQueryDetailUnavailable", { package: packageLabel })}
        </Typography>
      </Box>
    );
  }

  if (error || type === "error") {
    // Quota / package codes keep dedicated UI above; everything else uses standard localized copy.
    return (
      <Alert severity="error" sx={rs.widgetDraftInvalidAlert}>
        <Typography component="div" sx={rs.errorAlertTitle}>
          {resolveChatClientErrorMessage(payload, t)}
        </Typography>
      </Alert>
    );
  }

  switch (type) {
    case "text": {
      const responseText = result != null ? getResponseText(result) : "";
      return (
        <Box>
          {message != null && message !== "" && (
            <Typography
              component="div"
              sx={rs.messageTitle}
            >
              {message}
            </Typography>
          )}
          <Typography
            component="div"
            sx={rs.messageBody}
          >
            {responseText}
          </Typography>
        </Box>
      );
    }

    case "table": {
      const normalized = result != null ? normalizeTablePayload(result) : null;
      const htmlFallback =
        !normalized && result != null && typeof getResponseHtml(result) === "string"
          ? getResponseHtml(result)
          : "";

      if (!normalized && !htmlFallback) {
        return (
          <Box>
            {message != null && message !== "" && (
              <Typography component="div" sx={rs.messageTitleCompact}>
                {message}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t("dashboard.noWidgetData")}
            </Typography>
          </Box>
        );
      }
      return (
        <Box
          sx={rs.fitContainerBox(fitContainer)}
        >
          {message != null && message !== "" && (
            <Typography
              component="div"
              sx={rs.messageTitleTable}
            >
              {message}
            </Typography>
          )}
          {normalized ? (
            <InteractiveTableView data={normalized} fitContainer={fitContainer} />
          ) : (
            <Box
              sx={rs.tableHtmlBox(fitContainer, darkMode)}
              dangerouslySetInnerHTML={{ __html: htmlFallback }}
            />
          )}
        </Box>
      );
    }

    case "chart": {
      if (result === undefined || result === null) {
        return null;
      }
      let option: Record<string, unknown> = {};
      if (typeof result === "string") {
        try {
          const parsed = JSON.parse(result) as Record<string, unknown>;
          option = (parsed.option != null ? parsed.option : parsed) as Record<string, unknown>;
        } catch {
          option = {};
        }
      } else if (typeof result === "object" && result !== null) {
        const r = result as Record<string, unknown>;
        option = (r.option != null ? r.option : r) as Record<string, unknown>;
      }
      option = normalizeChartOption(option);
      const { height: chartHeight, minWidth: chartMinWidth } = chartLayoutSize(option);
      const hasSeries =
        Array.isArray((option as any)?.series) && ((option as any).series as any[]).length > 0;

      if (!hasSeries) {
        return (
          <Typography component="div" sx={rs.chartWarning}>
            {t("chat.chartRenderError")}
          </Typography>
        );
      }

      return (
        <Box sx={rs.chartOuterBox(fitContainer)}>
          {message != null && message !== "" && (
            <Typography component="div" sx={rs.chartMessageTitle(fitContainer)}>
              {message}
            </Typography>
          )}
          <Box sx={rs.chartInnerBox(fitContainer, chartHeight, chartMinWidth)}>
            <ChartErrorBoundary errorSx={rs.chartErrorText} fallbackMessage={t("chat.chartRenderError")}>
              <ReactECharts
                option={option}
                style={rs.chartEchartsStyle(fitContainer, chartHeight, chartMinWidth)}
                notMerge
                opts={{ renderer: "canvas" }}
                onEvents={
                  onChartClick
                    ? {
                        click: (params: { name?: string }) => {
                          if (params?.name != null && String(params.name).trim() !== "") {
                            onChartClick(String(params.name));
                          }
                        },
                      }
                    : undefined
                }
              />
            </ChartErrorBoundary>
          </Box>
        </Box>
      );
    }

    case "json": {
      if (isForecastResultPayload(result)) {
        return (
          <Box>
            {message != null && message !== "" && (
              <Typography component="div" sx={rs.messageTitleCompact}>
                {message}
              </Typography>
            )}
            <ForecastResultView result={result} />
          </Box>
        );
      }
      const pretty =
        result != null
          ? typeof result === "string"
            ? (() => {
                try {
                  return JSON.stringify(JSON.parse(result), null, 2);
                } catch {
                  return result;
                }
              })()
            : JSON.stringify(result, null, 2)
          : "";
      return (
        <Box>
          {message != null && message !== "" && (
            <Typography component="div" sx={rs.messageTitleCompact}>
              {message}
            </Typography>
          )}
          <Typography component="pre" sx={rs.jsonPre(darkMode)}>
            {pretty}
          </Typography>
        </Box>
      );
    }

    case "html": {
      const doc = result != null ? getResponseHtml(result) : "";
      const themedDoc = doc.trim() !== "" ? themeHtmlReportDocument(doc, darkMode) : "";
      return (
        <Box>
          {message != null && message !== "" && (
            <Typography component="div" sx={rs.messageTitleCompact}>
              {message}
            </Typography>
          )}
          {themedDoc ? (
            <Box
              component="iframe"
              title={t("chat.reportFrameTitle")}
              srcDoc={themedDoc}
              sandbox="allow-same-origin"
              sx={rs.htmlIframe(darkMode)}
            />
          ) : (
            <Typography component="div" sx={rs.htmlWarning}>
              {t("chat.noHtmlContent")}
            </Typography>
          )}
        </Box>
      );
    }

    case "clarification":
      return (
        <ClarificationPanel
          message={message}
          result={result}
          composerText={clarificationComposerText}
          originalRequest={clarificationOriginalRequest}
          onSubmit={onClarificationAnswer}
        />
      );

    case "widget-draft":
      return <WidgetDraftPanel message={message} payload={payload} onWidgetDraftPersisted={onWidgetDraftPersisted} />;

    case "mutation-draft":
      return (
        <ChatFeatureGate feature="mutations">
          <MutationDraftPanel message={message} payload={payload} />
        </ChatFeatureGate>
      );

    case "forecast-plan":
      return (
        <ChatFeatureGate feature="forecast">
          <ForecastPlanPanel message={message} payload={payload} />
        </ChatFeatureGate>
      );

    case "forecast-result":
      return (
        <Box>
          {message != null && message !== "" && (
            <Typography component="div" sx={rs.messageTitleCompact}>
              {message}
            </Typography>
          )}
          <ForecastResultView result={result} />
        </Box>
      );

    default: {
      if (payload.type === "widget-draft" || resolveWidgetDraft(payload)) {
        return <WidgetDraftPanel message={message} payload={payload} onWidgetDraftPersisted={onWidgetDraftPersisted} />;
      }
      if (payload.type === "mutation-draft" || payload.mutationDraft) {
        return (
          <ChatFeatureGate feature="mutations">
            <MutationDraftPanel message={message} payload={payload} />
          </ChatFeatureGate>
        );
      }
      if (payload.type === "forecast-plan" || payload.forecastPlan) {
        return (
          <ChatFeatureGate feature="forecast">
            <ForecastPlanPanel message={message} payload={payload} />
          </ChatFeatureGate>
        );
      }

      const text = result != null ? (typeof result === "string" ? result : JSON.stringify(result, null, 2)) : "";
      return (
        <Typography
          component="pre"
          sx={rs.defaultPre}
        >
          {text}
        </Typography>
      );
    }
  }
}
